import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import {
  Chip,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { UserStatusChip } from 'components/UserStatusChip';
import { useProgressionSourcesWithModels } from 'features/wildfires/hooks/useProgressionSourcesWithModels';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { getTimeAgo } from 'shared/dates';

type ProgressionModelsListProps = {
  wildfireId: number;
  title?: ReactNode;
  excludeModel?: string;
};

export const ProgressionModelsList = (
  props: ProgressionModelsListProps,
): JSX.Element | null => {
  const { wildfireId, title, excludeModel } = props;
  const { t } = useTranslation();
  const { progressionSourcesWithModels, isLoading } =
    useProgressionSourcesWithModels({ wildfireId });

  const sourcesWithModels = excludeModel
    ? progressionSourcesWithModels.filter(
        (sourceWithModel) =>
          sourceWithModel.sourceAttribution !== excludeModel &&
          sourceWithModel.models.length > 0,
      )
    : progressionSourcesWithModels;

  if (!isLoading && sourcesWithModels.length === 0) {
    return null;
  }

  return (
    <List
      subheader={
        <ListSubheader variant="watchduty-section-header">
          {title ?? (
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h3">
                {t('geoEvent.structuredInfo.progressionModels')}
              </Typography>
              <UserStatusChip type="pro" />
            </Stack>
          )}
        </ListSubheader>
      }
      disablePadding
    >
      {isLoading && (
        <ListItem divider role="status">
          <ListItemText
            primary={<Skeleton variant="text" width={80} />}
            secondary={<Skeleton variant="text" width="80%" />}
          />
        </ListItem>
      )}

      {sourcesWithModels.map((sourceWithModel) => {
        const { sourceAttribution, models } = sourceWithModel;
        const model = models[0]; // Use the first (newest) model
        return (
          <ListItemButton
            key={sourceAttribution}
            divider
            sx={{ '&.Mui-disabled': { opacity: 1 } }}
            component={Link}
            to={{
              pathname: `/i/${wildfireId}/models/${model?.id}`,
              state: {
                skipMapCentering: true,
                // Skip resetting layers config if excludeModel is set - we're already inside a model page
                skipSetLayersConfig: Boolean(excludeModel),
              },
            }}
            disabled={!model}
          >
            <ListItemText
              primary={
                <>
                  <Chip
                    label={
                      model
                        ? t('geoEvent.structuredInfo.available')
                        : t('geoEvent.structuredInfo.unavailable')
                    }
                    variant="watchduty"
                    color={model ? 'success' : 'secondary'}
                  />
                  <Typography sx={{ fontWeight: 'fontWeightMedium' }}>
                    {sourceAttribution}
                  </Typography>
                </>
              }
              secondary={
                model
                  ? t('geoEvent.structuredInfo.updatedAgo', {
                      date: getTimeAgo(model.dateModified.toISOString()),
                    })
                  : undefined
              }
            />
            {Boolean(model) && (
              <ListItemIcon
                sx={(theme) => ({
                  minWidth: 'fit-content',
                  color: theme.palette.text.primary,
                })}
              >
                <KeyboardArrowRightIcon />
              </ListItemIcon>
            )}
          </ListItemButton>
        );
      })}
    </List>
  );
};
